import React, { Fragment, memo } from 'react';

import { useSelector } from 'react-redux';

import clsx from 'clsx';

import { useFeatures } from '@atc/features';

import {
    Section,
    SeeMore,
} from 'reaxl';

import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import { KBBDealerRatings } from '@/components/RatingsReviews';
import DealerTitle from '@/components/vehicledetails/DealerTitle';

import DealerDisclosureContainer from './DealerDisclosureContainer';

const renderSellerComments = (vehicleDescription, sellerComments) => {
    const content = (
        <>
            {vehicleDescription
                && (
                    <p
                        className="text-left"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: vehicleDescription }}
                    />
                )}
            {sellerComments
                && (
                    <p
                        className="text-left"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: sellerComments }}
                    />
                )}
            <DealerDisclosureContainer />
        </>
    );
    return (
        <SeeMore>
            {content}
        </SeeMore>
    );
};

function SellerCommentsHeading({ children }) {
    return (
        <Fragment key="sellerCommentsHeading">
            More from the seller
            {children}
        </Fragment>
    );
}

function StockNumberAndVIN({ stockNumber, vin, isRestyled }) {
    const hasStockNum = stockNumber && !stockNumber.startsWith('Info');

    return (hasStockNum || vin) ? (
        <div className={clsx(
            'text-gray-dark text-normal',
            {
                'text-size-300 text-size-sm-400 margin-bottom-2': isRestyled,
                'text-size-300': !isRestyled,
            }
        )}
        >
            {hasStockNum && (
                <span className="display-inline-block">
                    Stock#:
                    {'\u00A0'}
                    {stockNumber}
                </span>
            )}
            {hasStockNum && vin && (
                <span className="text-gray-dark padding-horizontal-4 display-none display-sm-inline-block">
                    |
                </span>
            )}
            {vin && (
                <span className="display-block display-sm-inline-block">
                    VIN:
                    {'\u00A0'}
                    {vin}
                </span>
            )}
        </div>
    ) : null;
}

function DealerDetailAndRating({ listing, defaultShowModal, isDealerRatingDisabled }) {
    const { owner = {}, dealerConfig, id: listingId } = listing;
    const { website, name, rating, id: ownerId } = owner;
    const isLink = website?.href && !(!!dealerConfig && dealerConfig?.displayOwnerSnapshotSiteLink === false);

    return (
        <div className="padding-bottom-3 text-size-500">
            <DealerTitle
                isLink={isLink}
                href={website?.href}
                analyticsData={{
                    inventoryId: listingId,
                    ownerId,
                    placement: 'snapshot',
                }}
                componentClass="h2"
            >
                {name}
            </DealerTitle>
            {!isDealerRatingDisabled && !!rating?.value && (
                <div className="text-normal">
                    <KBBDealerRatings
                        className="margin-top-2"
                        owner={owner}
                        widgetType="inline"
                        showCount
                        enableImpression
                        defaultShowModal={defaultShowModal}
                    />
                </div>
            )}
        </div>
    );
}

export function AdditionalVehicleDetailsContainer() {
    const {
        dealer_name_relocation: [isDealerNameRelocationFlagEnabled],
        dealer_rating: [isDealerRatingEnabled],
    } = useFeatures([
        'dealer_name_relocation',
        'dealer_rating',
    ]);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const showKBBDealerModal = useSelector(vdpActiveInteractionDuck.selectors.getShowKBBDealerModal);

    const stockNumber = listing?.stockNumber || '';
    const { owner = {}, vin } = listing ?? {};
    const { disclaimer, sellerComments, privateSeller: isPrivateSeller = false } = owner ?? {};
    const vehicleDescription = listing?.fullDescription;
    const isDealerNameRelocation = isDealerNameRelocationFlagEnabled && !isPrivateSeller;

    let showSellerComments = stockNumber || sellerComments || vehicleDescription || disclaimer;

    if (!showSellerComments && vin) {
        showSellerComments = true;
    }

    if (!showSellerComments) {
        return null;
    }

    const sellerCommentsContent = (sellerComments || vehicleDescription)
        ? renderSellerComments(vehicleDescription, sellerComments) : <DealerDisclosureContainer />;

    const stockNumberAndVinComponent = (
        <StockNumberAndVIN
            stockNumber={stockNumber}
            vin={vin}
            isRestyled={isDealerNameRelocation}
        />
    );

    return (
        <Section
            heading={(
                <SellerCommentsHeading>
                    {!isDealerNameRelocation && stockNumberAndVinComponent}
                </SellerCommentsHeading>
            )}
            alignment="left"
            uiContext="level-3"
            id="sellerComments"
            className="padding-vertical-4 padding-vertical-md-5"
        >
            {isDealerNameRelocation && (
                <>
                    <DealerDetailAndRating
                        listing={listing}
                        showKBBDealerModal={showKBBDealerModal}
                        isDealerRatingDisabled={!isDealerRatingEnabled}
                    />
                    {stockNumberAndVinComponent}
                </>
            )}
            {sellerCommentsContent}
        </Section>
    );
}

export default memo(AdditionalVehicleDetailsContainer);
